import { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { PriceReport } from "../../api/priceReport";
import { fetchJson } from "../../lib/api";
import { dataContextGuard } from "../../lib/dataContext";

/** Mounted only for a successfully rendered overview, never a position/deep link.
 * The immutable visit survives back/reload; ACK advances only the personal cursor.
 */
export default function PriceReportNewTasks({ report, summaryParams }: { report: PriceReport; summaryParams: URLSearchParams }) {
  const freshness = report.freshness;
  const visit = freshness?.visit_id;
  const { mutate, isError, isPending } = useMutation({
    mutationKey: ["price-report-ack", visit],
    mutationFn: async (id: string) => {
      const guard = dataContextGuard(); guard();
      await fetchJson(`/api/reports/price-report/visits/${id}/ack`, { method: "POST" });
      guard();
    },
  });
  useEffect(() => { if (visit) mutate(visit); }, [visit, mutate]);
  if (!freshness) return null;
  const next = new URLSearchParams(summaryParams); next.set("review", "1"); next.set("new_tasks", "1");
  return <section aria-label="Neue Prüfaufgaben" className="-mt-3 mb-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600">
    <p>{!visit ? "Der Neu-Vergleich ist nach der nächsten Datenaktualisierung verfügbar." : freshness.since === null ? "Erster Bericht für diese Auswahl. Die offenen Aufgaben bilden deinen Ausgangsstand." : `Seit deinem letzten Preisbericht: ${freshness.new_count} neue offene ${freshness.new_count === 1 ? "Prüfaufgabe" : "Prüfaufgaben"} in dieser Auswahl.`}</p>
    {visit && freshness.new_count > 0 && <Link className="font-medium text-emerald-700 underline underline-offset-4" to={`/reports/variance?${next}`}>Neue Aufgaben ansehen →</Link>}
    {isError && <p role="status" className="basis-full">Der Berichtsstand konnte nicht gemerkt werden. Deine Prüfentscheidungen bleiben erhalten. <button disabled={isPending} className="underline" onClick={() => visit && mutate(visit)}>Erneut merken</button></p>}
  </section>;
}
